import { useState } from "react";
import { API_BASE } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import { FaDownload, FaUpload, FaFileExcel, FaInfoCircle } from "react-icons/fa";

export default function ExcelImportExport() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { getAuthHeaders } = useAuth();

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await fetch(`${API_BASE}/excel/export`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patients_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select a valid Excel file (.xlsx or .xls)');
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_BASE}/excel/import`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders().Authorization
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setImportResults(result.results);
        alert(`Import completed!\n${result.results.success} patients imported successfully\n${result.results.failed} failed`);
        
        // Clear file selection
        setSelectedFile(null);
        document.getElementById('file-input').value = '';
      } else {
        alert(`Import failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing data. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <FaFileExcel className="text-3xl text-green-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            Excel Import/Export
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Export Section */}
          <div className="bg-green-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              <FaDownload />
              Export Patients to Excel
            </h3>
            <p className="text-gray-600 mb-4">
              Download all patient data as an Excel file. This file can be used for offline work.
            </p>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <FaDownload />
                  Export to Excel
                </>
              )}
            </button>
          </div>

          {/* Import Section */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <FaUpload />
              Import Patients from Excel
            </h3>
            <p className="text-gray-600 mb-4">
              Upload an Excel file to import patient data. Registration numbers will be auto-generated.
            </p>
            
            <div className="space-y-4">
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              
              <button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <FaUpload />
                    Import from Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Import Results */}
        {importResults && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">Import Results</h4>
            <div className="text-sm text-gray-600">
              <p>✅ Successfully imported: {importResults.success} patients</p>
              <p>❌ Failed to import: {importResults.failed} patients</p>
              
              {importResults.errors.length > 0 && (
                <div className="mt-3">
                  <p className="font-semibold text-red-600">Errors:</p>
                  <ul className="list-disc list-inside text-red-600 mt-1">
                    {importResults.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {importResults.errors.length > 5 && (
                      <li>... and {importResults.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Detailed Failed Records */}
              {importResults.failedRecords && importResults.failedRecords.length > 0 && (
                <div className="mt-4">
                  <p className="font-semibold text-orange-600">Failed Records Details:</p>
                  <div className="mt-2 space-y-2">
                    {importResults.failedRecords.slice(0, 3).map((record, index) => (
                      <div key={index} className="bg-red-50 p-3 rounded border-l-4 border-red-400">
                        <p className="font-medium text-red-800">
                          Row {record.row}: {record.name}
                        </p>
                        <p className="text-red-600 text-xs mt-1">{record.error}</p>
                        {record.data && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 cursor-pointer">
                              View record data
                            </summary>
                            <pre className="text-xs bg-gray-100 p-2 mt-1 rounded overflow-x-auto">
                              {JSON.stringify(record.data, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                    {importResults.failedRecords.length > 3 && (
                      <p className="text-sm text-gray-500">
                        ... and {importResults.failedRecords.length - 3} more failed records
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
          <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
            <FaInfoCircle />
            Instructions
          </h4>
          <div className="text-sm text-gray-700 space-y-2">
            <p><strong>Export:</strong> Downloads all patient data as password-protected Excel file for offline work. Registration numbers are included for reference.</p>
            <p><strong>Import:</strong> Upload Excel file to add new patients. Registration numbers are auto-generated (ignores existing numbers in Excel).</p>
            <p><strong>Required Fields:</strong> First Name, Age, Gender</p>
            <p><strong>Optional Fields:</strong> All other patient information</p>
            <p><strong>File Format:</strong> .xlsx or .xls files only</p>
            <p><strong>Note:</strong> When importing, new registration numbers will be automatically generated for all patients.</p>
            <p><strong>Security:</strong> Exported files are password protected with password: <code className="bg-gray-200 px-1 rounded">AHC2025</code></p>
          </div>
        </div>
      </div>
    </div>
  );
} 